import { Observable } from 'rxjs';
import { FormGroup, NgForm } from '@angular/forms';
import { Component, OnInit, ViewChild, EventEmitter, Output, Input } from '@angular/core';
//grid-application-shared
import { DocumentFormSections } from '../../entities/enums/ui-collection-types.enum';
import { DocumentsSectionsState } from '../../entities/sections-state/documentsSectionsState';
import { EditabilityBasedOnProcessStatus } from '../../entities/editabilityBasedOnProcessStatus';
import { GridApplicationDocumentTypes } from '../../entities/enums/grid-application-document-types.enum';
//common-shared
import { FileEvent } from '../../../+shared/models/fileEvent';
import { EnvironmentService } from '../../../services/environment.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ContactPerson, Document, LookupOptionsData, UpdateApplicationStatusRequest } from '../../../jurisdiction/jurisdiction.domain';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { AllDropdownKeys } from 'src/app/jurisdiction/jurisdiction.constants';
import { CommonService } from 'src/app/services/common.service';
import { GridCompatibilityOrderProcessService } from 'src/app/+grid-compatibility/services/grid-compatibility-order-process.service';
import { RegepProcessStatus } from 'src/app/+regep/entities/enums/regepProcessStatus';

@Component({
  selector: 'app-documents-form',
  templateUrl: './documents-form.component.html',
  styleUrls: ['./documents-form.component.scss'],
})
export class DocumentsFormComponent extends DestroyableComponent implements OnInit {
  @ViewChild('documentsData', { read: NgForm }) documentsData: NgForm;

  @Input() isLoading: boolean = false;
  @Input() isDetailsView: boolean = false;
  @Input() application_id: number;
  @Input() externalApplicationId: string;
  @Input() requiredDocuments: Document[];
  @Input() otherDocuments: Document[];
  @Input() isLockUnlockButtonClicked: boolean = false;
  @Input() commentSectionOrderProcess$: Observable<any>;
  @Input() commentSectionEditableStatuses: Array<number>;
  @Input() updateRequest: UpdateApplicationStatusRequest;
  @Input() documentsSectionsState: DocumentsSectionsState;
  @Input() editabilityBasedOnProcessStatus: EditabilityBasedOnProcessStatus;
  @Input() applicationTypeText: string;
  @Input() contactPersonData: ContactPerson;
  @Input() applicationOwnerId: any;
  @Input() applicationSkStatus: any;

  @Output() updateComment = new EventEmitter<string>();
  @Output() clearFile = new EventEmitter<FileEvent>();
  @Output() removeDocument = new EventEmitter<number>();
  @Output() change = new EventEmitter<void>();
  @Output() contactPersonFormInit = new EventEmitter<FormGroup>();

  public role: string;
  public isDocumentsCommentVisible: boolean = false;
  public documentTypes = GridApplicationDocumentTypes;
  public documentLookups: LookupOptionsData[];
  public DocumentFormSections = DocumentFormSections;
  public orderProcess: any;
  public showContactPersonOnDocumentsPage: boolean = this.environmentService.showContactPersonOnDocumentsPage;

  constructor(
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private commonService: CommonService,
    private gridCompatibilityOrderProcessService: GridCompatibilityOrderProcessService
  ) {
    super();
  }

  ngOnInit(): void {
    this.role = this.localStorageService.getItem('role');
    this.documentsData?.form.valueChanges.subscribe(value => this.change.emit());
    this.documentLookups = this.commonService.getDropdownData(AllDropdownKeys.DocumentType);
    this.gridCompatibilityOrderProcessService.orderProcess$.subscribe(orderProcess => (this.orderProcess = orderProcess));
  }

  public addDocument(): void {
    this.otherDocuments.push(new Document(GridApplicationDocumentTypes.Other));
    this.documentsData.form.controls['comment']?.markAsDirty();
    this.change.emit();
  }

  public lockSection(): void {
    if (!this.isModeratorRole()) {
      return;
    }

    this.documentsSectionsState.is_documents_locked = this.documentsSectionsState.is_documents_locked ? null : true;
    this.isLockUnlockButtonClicked = true;
    this.change.emit();
  }

  public unlockSection(): void {
    if (!this.isModeratorRole()) {
      return;
    }

    if (this.documentsSectionsState.is_documents_locked === false) {
      this.documentsSectionsState.is_documents_locked = null;
    } else {
      this.documentsSectionsState.is_documents_locked = false;

      if (!this.isDocumentsCommentVisible) {
        this.showComment();
      }
    }

    this.isLockUnlockButtonClicked = true;
    this.change.emit();
  }

  public showComment(): void {
    if (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) {
      this.isDocumentsCommentVisible = !(this.isDocumentsCommentVisible && !this.documentsSectionsState.documents_comment);
    }
  }

  public isDisabled(): boolean {
    if (this.isTechnicianRole()) {
      return !this.editabilityBasedOnProcessStatus.technicianRole;
    }
    if (this.isCustomerRole()) {
      return !this.editabilityBasedOnProcessStatus.customerRole;
    }

    return !this.environmentService.shouldInternalRolesBeAbleToEditApplication && (this.isModeratorRole() || this.role === 'observer');
  }

  public isModeratorRole(): boolean {
    return this.role === 'employee' || this.role === 'administrator';
  }

  public isTechnicianRole(): boolean {
    return this.role === 'standard_technician' || this.role === 'guest_technician';
  }

  public isCustomerRole(): boolean {
    return this.role === 'customer';
  }

  public trackBy(i: number): number {
    return i;
  }

  public isDocumentDisabled(): boolean {
    return this.documentsSectionsState?.is_documents_locked !== true && this.isDisabled() && this.isDetailsView;
  }

  public emitUpdateComment(comment: string) {
    this.updateComment.emit(comment);
  }

  public markCommentAsDirty() {
    this.documentsData.form.controls['comment']?.markAsDirty();
  }

  public emitClearFile(fileEvent: FileEvent) {
    this.markCommentAsDirty();
    this.clearFile.emit(fileEvent);
  }

  public emitRemoveDocument(index: number) {
    this.markCommentAsDirty();
    this.removeDocument.emit(index);
  }

  public getDocumentLabel(documentType: string) {
    const label = this.documentLookups.find(l => l.value === documentType)?.label;
    if (label) return label;
    return 'documents.' + documentType;
  }

  public onContactPersonInitForm(form: FormGroup) {
    this.contactPersonFormInit.emit(form);
  }

  public checkIfIsEditable() {
    return data => {
      const { isSectionRejected } = data;

      return (
        !this.isDetailsView ||
        (isSectionRejected &&
          this.orderProcess?.status === RegepProcessStatus.EngineeringOpenIssue &&
          this.applicationOwnerId?.toString() === this.localStorageService.getItem('technician_id'))
      );
    };
  }
}
